import { CopyableField } from "@/components/copyable-field";
import { DEFAULT_JFROG_TOKEN_DURATION, JFrogTokenDurationSelect } from "@/components/jfrog-token-duration-select";
import { JFrogTokenReferenceCheckbox } from "@/components/jfrog-token-reference-checkbox";
import { useServiceAccountStatus } from "@/components/service-account-dialog";
import { useCreateJFrogToken } from "@/hooks/use-jfrog-tokens";
import { formatAssetField, formatPullCommand } from "@/lib/asset-utils";
import { toast } from "@/lib/instrumented-toast";
import { getJFrogTokenExpiryDays, type JFrogTokenDuration } from "@/lib/jfrog-token-duration";
import { formatDate } from "@/lib/utils";
import { useOrgStore } from "@/stores";
import { Asset } from "@/stores/asset-store";
import { Check, ChevronDownIcon, ChevronRightIcon, Clipboard, CopyIcon, DownloadIcon, Info } from "lucide-react";
import React from "react";
import { Link } from "react-router";

import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Separator } from "./ui/separator";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "./ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

function buildRegistryAuthCommands(asset: Asset, saUsername: string, token: string | undefined) {
  const isRegistryAuthAsset =
    !!asset.pull_command && (asset.asset_group === "helm charts" || asset.asset_group === "docker images");

  if (!isRegistryAuthAsset) {
    return {
      isRegistryAuthAsset: false,
      isDockerAsset: false,
      authLoginCmd: "",
      authPullCmd: "",
      authCommandBlock: "",
    };
  }

  const isDockerAsset = asset.asset_group === "docker images";
  const authTokenPlaceholder = token ?? "[TOKEN]";
  const authLoginCmd = isDockerAsset
    ? `docker login istaridigital.jfrog.io -u ${saUsername} -p ${authTokenPlaceholder}`
    : `helm registry login -u ${saUsername} -p ${authTokenPlaceholder} istaridigital.jfrog.io`;
  const authPullCmd = formatPullCommand(asset);

  return {
    isRegistryAuthAsset: true,
    isDockerAsset,
    authLoginCmd,
    authPullCmd,
    authCommandBlock: `${authLoginCmd}\n${authPullCmd}`,
  };
}

interface AssetMetadataDrawerProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload?: (asset: Asset) => void;
}

export function AssetMetadataDrawer({ asset, open, onOpenChange, onDownload }: AssetMetadataDrawerProps) {
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());
  const [generatedTokenData, setGeneratedTokenData] = React.useState<{
    token: string;
    referenceToken?: string;
    tokenReferenceId: string;
    expiresAt: string | null;
  } | null>(null);
  const [tokenDuration, setTokenDuration] = React.useState<JFrogTokenDuration>(DEFAULT_JFROG_TOKEN_DURATION);
  const [includeReferenceToken, setIncludeReferenceToken] = React.useState(false);
  const selectedOrgId = useOrgStore.use.selectedOrgId();
  const selectedOrg = useOrgStore.use.selectedOrg();
  const createTokenMutation = useCreateJFrogToken();

  const currentOrgId = React.useMemo(() => {
    return selectedOrg?.id || selectedOrgId || "";
  }, [selectedOrg, selectedOrgId]);

  const saQuery = useServiceAccountStatus(currentOrgId, open && !!currentOrgId);
  const hasSA = saQuery.isSuccess && saQuery.data !== null && saQuery.data !== undefined;
  const noSA = saQuery.isSuccess && saQuery.data === null;
  const saUsername = saQuery.data?.jfrog_username ?? "[USERNAME]";

  React.useEffect(() => {
    setGeneratedTokenData(null);
    setTokenDuration(DEFAULT_JFROG_TOKEN_DURATION);
    setIncludeReferenceToken(false);
  }, [asset?.asset_path, currentOrgId]);

  const handleGenerateCommands = async () => {
    if (!currentOrgId) return;
    try {
      const result = await createTokenMutation.mutateAsync({
        orgId: currentOrgId,
        request: {
          organization_id: currentOrgId,
          expires_in_days: getJFrogTokenExpiryDays(tokenDuration),
          ...(includeReferenceToken && { include_reference_token: true }),
        },
      });
      if (result.token_value) {
        setGeneratedTokenData({
          token: result.token_value,
          referenceToken: result.reference_token,
          tokenReferenceId: result.jfrog_token_id,
          expiresAt: result.expires_at ?? null,
        });
      }
    } catch {
      // error toast handled by the hook
    }
  };

  if (!asset) return null;

  const { isRegistryAuthAsset, isDockerAsset, authLoginCmd, authPullCmd, authCommandBlock } = buildRegistryAuthCommands(
    asset,
    saUsername,
    generatedTokenData?.referenceToken ?? generatedTokenData?.token,
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const copyAsJson = () => {
    const jsonString = JSON.stringify(asset, null, 2);
    navigator.clipboard.writeText(jsonString);
    toast.success("Asset metadata copied as JSON");
  };

  const metadataSections = [
    {
      id: "basic",
      title: "Basic Information",
      fields: [
        { label: "Asset Name", value: asset.asset_name },
        { label: "Description", value: asset.asset_description },
        { label: "Download Name", value: asset.download_name },
        { label: "Size", value: asset.size },
        { label: "Agent Installer Type", value: asset.installer_type },
      ],
    },
    ...(asset.pull_command
      ? [
          {
            id: "pull",
            title: "Pull Command",
            fields: [{ label: "Command", value: formatPullCommand(asset) }],
          },
        ]
      : []),
    {
      id: "version",
      title: "Version Information",
      fields: [
        { label: "Display Version", value: asset.display_version },
        { label: "Semantic Version", value: asset.semantic_version },
        { label: "Calendar Version", value: asset.calendar_version },
        { label: "Display Release Month", value: asset.display_release_month },
      ],
    },
    {
      id: "checksums",
      title: "Checksums",
      fields: [
        { label: "SHA-256", value: asset.sha256 },
        { label: "SHA-1", value: asset.sha1 },
      ],
    },
    {
      id: "technical",
      title: "Technical Details",
      fields: [
        // Hide Architecture and OS for Docker images and Helm charts
        ...(asset.asset_group !== "docker images" && asset.asset_group !== "helm charts"
          ? [
              { label: "Architecture", value: asset.architecture },
              { label: "Operating System", value: asset.operating_system },
            ]
          : []),
        { label: "Asset Type", value: asset.asset_type },
        { label: "Asset Category", value: asset.asset_category },
        { label: "Asset Key", value: asset.asset_key },
      ],
    },
    {
      id: "classification",
      title: "Classification",
      fields: [
        { label: "Asset Group", value: asset.asset_group },
        { label: "Asset Subgroup", value: asset.asset_subgroup },
      ],
    },
    {
      id: "release",
      title: "Release Information",
      fields: [
        { label: "Release Year", value: asset.release_year },
        { label: "Release Month", value: asset.release_month },
        { label: "Release Day", value: asset.release_day },
      ],
    },
    {
      id: "location",
      title: "File Location",
      fields: [{ label: "Asset Path", value: asset.asset_path }],
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange} key={`sheet-${currentOrgId}`}>
      <SheetContent className="w-[400px] overflow-y-auto sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Asset Details</SheetTitle>
        </SheetHeader>

        {/* Action Buttons - Only show for non-Helm/Docker assets */}
        {asset.asset_group !== "helm charts" && asset.asset_group !== "docker images" && onDownload && (
          <div className="mt-4 flex flex-col gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">
                  <Button variant="default" className="w-full" onClick={() => onDownload(asset)}>
                    <DownloadIcon className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                </span>
              </TooltipTrigger>
              {noSA && (
                <TooltipContent>
                  No service account is configured for this organization; downloads are disabled until a service account
                  is created.
                </TooltipContent>
              )}
              {saQuery.isError && <TooltipContent>Unable to verify service account status</TooltipContent>}
            </Tooltip>
            {noSA && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Info className="h-4 w-4 shrink-0" />
                No service account is configured. For improved security and auditing, a superadmin can create a service
                account for this organization.
              </p>
            )}
            {saQuery.isError && (
              <p className="flex items-center gap-1.5 text-sm text-destructive">
                <Info className="h-4 w-4 shrink-0" />
                Unable to verify service account status. Downloads may fail if the service account is not configured.
              </p>
            )}
          </div>
        )}

        {/* Authentication Information for Helm/Docker */}
        {isRegistryAuthAsset && (
          <div className="mt-2 space-y-3">
            {/* Usage Instructions */}
            <div className="rounded-md bg-muted p-4">
              <p className="mb-3 text-sm font-medium">Usage Instructions:</p>
              <div className="space-y-4 text-xs">
                <div key={`${isDockerAsset ? "docker" : "helm"}-${currentOrgId}`} className="space-y-4">
                  {noSA && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Info className="h-3 w-3 shrink-0" />A superadmin must create a service account for this
                      organization.
                    </p>
                  )}

                  {!generatedTokenData?.token && (
                    <div className="space-y-3">
                      <JFrogTokenDurationSelect value={tokenDuration} onValueChange={setTokenDuration} />
                      <JFrogTokenReferenceCheckbox
                        checked={includeReferenceToken}
                        onCheckedChange={setIncludeReferenceToken}
                        disabled={!currentOrgId || !hasSA || createTokenMutation.isPending}
                      />
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleGenerateCommands}
                              disabled={!currentOrgId || !hasSA || createTokenMutation.isPending}
                            >
                              {createTokenMutation.isPending ? "Generating..." : "Generate ready-to-use commands"}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {noSA && <TooltipContent>No service account configured for this organization</TooltipContent>}
                      </Tooltip>
                    </div>
                  )}

                  {generatedTokenData && (
                    <Alert className="border-green-500/50 bg-green-50 dark:bg-green-950/20">
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="space-y-3 text-green-800 dark:text-green-200">
                        <p>Token generated — copy these credentials now. The token values will not be shown again.</p>
                        <CopyableField label="Username" value={saUsername} mono />
                        <CopyableField label="Token ID" value={generatedTokenData.tokenReferenceId} mono />
                        {generatedTokenData.referenceToken && (
                          <CopyableField label="Reference token" value={generatedTokenData.referenceToken} mono />
                        )}
                        <CopyableField label="Token" value={generatedTokenData.token} mono />
                        {generatedTokenData.expiresAt && (
                          <CopyableField label="Expires" value={formatDate(generatedTokenData.expiresAt)} />
                        )}
                        <Link to="/jfrog-tokens" className="text-xs underline">
                          Manage all tokens on JFrog Registry Tokens page
                        </Link>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <p className="font-medium text-foreground">
                        {isDockerAsset ? "Login and pull" : "Login and install"}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => copyToClipboard(authCommandBlock, "Commands")}
                      >
                        <Clipboard className="mr-1 h-3 w-3" />
                        Copy all
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      <div className="relative">
                        <code className="block overflow-x-auto whitespace-pre-wrap break-all rounded bg-background p-2 pr-10 font-mono">
                          {authLoginCmd}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1 h-7 px-2"
                          onClick={() =>
                            copyToClipboard(authLoginCmd, isDockerAsset ? "Docker login command" : "Helm login command")
                          }
                        >
                          <Clipboard className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="relative">
                        <code className="block overflow-x-auto rounded bg-background p-2 pr-10 font-mono">
                          {authPullCmd}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1 h-7 px-2"
                          onClick={() =>
                            copyToClipboard(authPullCmd, isDockerAsset ? "Pull command" : "Install command")
                          }
                        >
                          <Clipboard className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <Separator className="my-4" />

        <p className="mb-4 text-sm text-muted-foreground">Complete metadata information for this asset</p>

        {/* Copy as JSON Button */}
        <div className="mb-4">
          <Button variant="outline" className="w-full" onClick={copyAsJson}>
            <CopyIcon className="mr-2 h-4 w-4" />
            Copy as JSON
          </Button>
        </div>

        <div className="space-y-2">
          {metadataSections.map((section) => (
            <Collapsible
              key={section.id}
              open={expandedSections.has(section.id)}
              onOpenChange={() => toggleSection(section.id)}
            >
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md bg-muted/50 px-3 py-2 transition-colors hover:bg-muted">
                <span className="text-sm font-semibold">{section.title}</span>
                {expandedSections.has(section.id) ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 px-3 py-2">
                {section.fields.map((field, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">{field.label}</span>
                      {field.value && field.value !== "" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => copyToClipboard(field.value || "", field.label)}
                        >
                          <CopyIcon className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <div className="text-sm">
                      {field.label === "Asset Category" ? (
                        <Badge variant="secondary">{formatAssetField(field.value)}</Badge>
                      ) : (
                        <span className="break-all">{formatAssetField(field.value)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        <Separator className="my-6" />

        <SheetFooter>
          <Button variant="outline" className="w-full" onClick={() => copyToClipboard(asset.asset_path, "Asset Path")}>
            <CopyIcon className="mr-2 h-4 w-4" />
            Copy Path
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
